import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Heading, Separator, Spinner } from 'telia-front-react';

import { v4 } from 'uuid';
import Config from '../Config';
import { DOCUMENT_CATEGORY, SERVICE_CATEGORY } from '../stores/SearchStore';
import { FetchStatus } from '../util/FetchStatus';
import SearchErrorNotice from './SearchErrorNotice';
import { SearchEvent, WebContentFilterOrder, WebContentResult } from './SearchInterfaces';
import SearchResult from './SearchResult';
import { SearchResultsProps } from './SearchResults';
import ShowMoreButton from './ShowMoreButton';

@observer
class ContentResults extends React.Component<SearchResultsProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  public render() {
    return <>{this.displayResults()}</>;
  }

  private displayResults(): JSX.Element | JSX.Element[] {
    const {
      searchStore: { searchWrappers, allContentResults },
      routeStore
    } = this.props;
    const translate = this.context.translate;
    const isSegmentedTab = this.isSegmentedTab();
    const documentResults: WebContentResult[] = this.getResults(isSegmentedTab, DOCUMENT_CATEGORY);
    const serviceResults: WebContentResult[] = this.getResults(isSegmentedTab, SERVICE_CATEGORY);

    if (allContentResults.status === 'ERROR') {
      return null;
    }
    if (isSegmentedTab) {
      if (
        allContentResults.status === FetchStatus.FETCHING ||
        [SERVICE_CATEGORY, DOCUMENT_CATEGORY].some((category) => searchWrappers[category].status === FetchStatus.FETCHING)
      ) {
        return <Spinner />;
      }
      if (!documentResults?.length && !serviceResults?.length) {
        return <p key={'notice'}>{translate('search.notFound')}</p>;
      }
    }
    const documentOrServiceResults = (documentResult: boolean) => {
      const category = documentResult ? DOCUMENT_CATEGORY : SERVICE_CATEGORY;
      const results = documentResult ? documentResults : serviceResults;
      return results.map((result, idx) => (
        <SearchResult
          result={result}
          key={result.objectID + idx}
          onClick={() => this.sendAlgoliaAnalyticsData(result, category)}
          routeStore={routeStore}
          translate={translate}
          contentClientTypeFilter={searchWrappers[category].filterOrder}
        />
      ));
    };

    const renderDocumentsOrServicesBlock = (documentResult: boolean) => {
      const category = documentResult ? DOCUMENT_CATEGORY : SERVICE_CATEGORY;
      return (
        <>
          <Heading id={documentResult ? 'search-results-documents' : 'search-results-services'} element="h2" className="h3">
            {documentResult ? translate('search.documents') : translate('search.services')}
          </Heading>
          <Separator spacing="xsmall" />
          {documentOrServiceResults(documentResult)}
          <ShowMoreButton category={category} separatorSpacing="xsmall" />
          <SearchErrorNotice showNoticeWhen={['ERROR', 'FETCH_MORE_ERROR']} status={searchWrappers[category].status} translate={translate} />
          {category === SERVICE_CATEGORY && <Separator spacing="medium" color="invisible" />}
        </>
      );
    };

    return (
      <>
        {!!serviceResults?.length && renderDocumentsOrServicesBlock(false)}
        {!!documentResults?.length && renderDocumentsOrServicesBlock(true)}
      </>
    );
  }

  private isSegmentedTab(): boolean {
    return [WebContentFilterOrder.PRIVATE, WebContentFilterOrder.BUSINESS].includes(this.props.searchStore.allContentResults.filterOrder);
  }
  private getResults = (isSegmentedTab: boolean, category: string): WebContentResult[] => {
    const { searchStore } = this.props;
    return isSegmentedTab ? searchStore.searchWrappers[category].segmentedResults : searchStore.searchWrappers[category].results;
  };

  private sendAlgoliaAnalyticsData = async (result: WebContentResult, category: string) => {
    const { searchStore } = this.props;
    const idxName = `${Config.componentTier}_web`;
    const searchEvent: SearchEvent = {
      eventName: 'Web search result click',
      eventType: 'click',
      index: idxName,
      objectIDs: [result.objectID],
      userToken: v4(),
      queryID: searchStore.searchWrappers[category].queryId,
      positions: [this.getResults(this.isSegmentedTab(), category).indexOf(result) + 1]
    };

    searchStore.postSearchTabAnalytics();
    searchStore.postSearchResultClickEvent(searchEvent);
  };
}

export default inject('searchStore', 'routeStore')(ContentResults);
